import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { v7 as uuidv7 } from "uuid";

import { AddNewCompanyCardPayloadType } from "../../../../../../../Pages/Companies/types";
import { inputFieldMarginBottom } from "../../../../../../../shared";
import InputField from "../../../../../../InputField/InputField";
import { DetailsDataType } from "../../../../../types";
import useCompaniesCardsFormInputs from "../../../../hooks/useCompaniesCardsFormInputs";

interface AddCompanyCardDialogContentProps {
  detailsData: DetailsDataType;
  setPayload: Dispatch<SetStateAction<AddNewCompanyCardPayloadType>>;
  setIsReadyToConfirm: Dispatch<SetStateAction<boolean>>;
  cardNameError: string | null;
  setCardNameError?: Dispatch<SetStateAction<string | null>>;
}

const AddCompanyCardDialogContent = ({
  detailsData,
  setPayload,
  setIsReadyToConfirm,
  cardNameError,
  setCardNameError
}: AddCompanyCardDialogContentProps) => {
  const { t } = useTranslation();

  const [cardName, setCardName] = useState<string>(detailsData?.name || "");
  const [cardId, setCardId] = useState<string | null>(null);
  const [cardNameInputError, setCardNameInputError] = useState<string | null>(
    null
  );
  const [cardIdInputError, setCardIdInputError] = useState<string | null>(null);

  const {
    isCardNameValid,
    validateCardNameLength,
    handleCardNameChangeValue,
    isCardIdValid,
    validateCardId,
    handleCardIdChangeValue,
    handleCardNameAxiosError
  } = useCompaniesCardsFormInputs({
    setCardNameInputError,
    setCardName,
    setCardIdInputError,
    setCardId,
    setCardNameError
  });

  const isPayloadReadyToConfirm =
    Boolean(detailsData?.id) &&
    Boolean(cardName) &&
    Boolean(cardId) &&
    Boolean(isCardIdValid(cardId)) &&
    Boolean(isCardNameValid(cardName));

  useEffect(() => {
    handleCardNameAxiosError(cardNameError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardNameError]);

  useEffect(() => {
    detailsData &&
      setPayload({
        companyId: detailsData.id,
        name: cardName.trim(),
        companyCardId: cardId
      });

    setIsReadyToConfirm(isPayloadReadyToConfirm);
  }, [
    cardId,
    cardName,
    detailsData,
    isPayloadReadyToConfirm,
    setIsReadyToConfirm,
    setPayload
  ]);

  return (
    <>
      <InputField
        id={uuidv7()}
        size="medium"
        data-testid="company-name"
        fullWidth
        readOnly
        labelLeft={t("Dialog##company")}
        placeholder={detailsData?.name}
        customStyle={inputFieldMarginBottom}
      />
      <InputField
        id={uuidv7()}
        testId="card-name-input-field"
        size="medium"
        fullWidth
        required
        errorText={cardNameInputError}
        onBlur={validateCardNameLength}
        onChange={handleCardNameChangeValue}
        labelLeft={t("Dialog##card name")}
        placeholder={t("Dialog##enter here")}
        customStyle={inputFieldMarginBottom}
        value={cardName}
      />
      <InputField
        id={uuidv7()}
        testId="card-id-input-field"
        size="medium"
        fullWidth
        required
        errorText={isCardIdValid(cardId) ? "" : cardIdInputError}
        onBlur={validateCardId}
        onChange={handleCardIdChangeValue}
        labelLeft={t("Dialog##company card id")}
        placeholder={t("Dialog##enter here")}
        customStyle={inputFieldMarginBottom}
        infoTooltip={t(`Dialog##enter company card id`)}
      />
    </>
  );
};

export default AddCompanyCardDialogContent;
